
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomProgressBar
 *
 * @file CustomProgressBar.jsx
 * @author Mohamed yasar arabath M
 */


import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';

const Bar = styled('div')({
  '&&': {
    borderRadius: '999px 0 0 999px'
  }
});

const Root = styled('div')(({ height }) => ({
  '&&': {
    position: 'relative',
    background: '#06111f',
    borderRadius: 999,
    height,
    minHeight: 12,
    width: '100%',
    padding: 2,
    display: 'flex',
    boxSizing: 'border-box'
  }
}));

const Bar1Buffer = styled(Bar)(({ value }) => ({
  '&&': {
    width: `${value}%`,
    background: '#18d6d6',
    zIndex: 2,
    borderRadius: value >= 100 ? '999px' : '999px 0 0 999px'
  }
}));

const Gap = styled('div')(({ gap }) => ({
  '&&': {
    width: gap,
    background: '#06111f',
    zIndex: 3
  }
}));

const Bar2Buffer = styled(Bar)(({ value, bufferValue, gap }) => ({
  '&&': {
    left: `calc(${value}% + ${gap}px)`,
    width: `calc(${bufferValue}% - ${gap}px)`,
    backgroundImage: 'repeating-linear-gradient(135deg, #f57a89 0px, #f57a89 12px, #ffb6b6 12px, #ffb6b6 24px)',
    zIndex: 1,
    borderRadius: (() => {
      if (value) {
        return 'unset';
      }

      return bufferValue >= 100 ? '999px' : '999px 0 0 999px';
    })()
  }
}));

const CustomProgressBar = memo(({ value, bufferValue, gap, height }) => (
  <Root height={height}>
    {value ? <Bar1Buffer value={value} /> : null}
    {value && bufferValue ? <Gap gap={gap} /> : null}
    {bufferValue ? <Bar2Buffer value={value} bufferValue={bufferValue} gap={gap} /> : null}
  </Root>
));

CustomProgressBar.defaultProps = {
  gap: 0,
  height: 12,
  value: 0,
  bufferValue: 0
};

CustomProgressBar.propTypes = {
  value: PropTypes.number,
  bufferValue: PropTypes.number,
  gap: PropTypes.number,
  height: PropTypes.number
};

export default CustomProgressBar;
